import React, { FC, ReactNode, useContext, useEffect, useState } from 'react'

import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { SKIP_ACCOUNT_CONNECTION } from '../constants'
import { APP_PATHS } from '../routes/paths'
import AuthService from '../services/AuthService'
import TokenService from '../services/TokenService'
import { TokensDataType } from '../services/types'
import { ServerResponse } from '../types'

import { UserPlatform } from './types'
import { getIsOnboadringCompleted, UserContext } from './userContext'
import { UserService } from './UserService'

export const ProvideAuth: FC<{ children: ReactNode }> = ({ children }) => {
  const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000
  const currentPlatform = Capacitor.getPlatform() as UserPlatform
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()

  const { setCurrentUser, setIsAccountConnected } = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(false)
  const [isSessionRestored, setIsSessionRestored] = useState(false)

  useEffect(() => {
    const restoreSession = async () => {
      if (AuthService.isAuth()) {
        try {
          setIsLoading(true)

          const tokenResponse = await TokenService.refreshToken(currentPlatform)

          if ((tokenResponse as ServerResponse<TokensDataType>)?.success) {
            const { data: userProfile } = await UserService.getUserProfile()
            const { data: userAccounts } = await UserService.getUserAccounts()

            const skipAccountConnection =
              localStorage.getItem(`${SKIP_ACCOUNT_CONNECTION}_${userProfile?.id}`) === 'true'

            const isOnboadringCompleted = getIsOnboadringCompleted(
              userProfile,
              !!userAccounts?.length || skipAccountConnection,
            )

            setCurrentUser(userProfile)
            setIsAccountConnected(!!userAccounts?.length)

            if (!isOnboadringCompleted) {
              navigate(APP_PATHS.onboarding)
            } else {
              const defaultPath = pathname === '/' ? APP_PATHS.dashboard : pathname

              const destination = searchParams.toString()
                ? `${defaultPath}?${createSearchParams(searchParams)}`
                : defaultPath

              navigate(destination)
            }
          }
        } catch (error) {
          AuthService.logout()
          setCurrentUser(null)
        } finally {
          setIsLoading(false)
          setIsSessionRestored(true)
        }
      } else {
        setIsSessionRestored(true)
      }
    }

    restoreSession()
  }, [])

  useEffect(() => {
    if (!isSessionRestored) {
      return
    }

    let heartbeatTimer: ReturnType<typeof setInterval> | null = null
    let removeAppStateListener: (() => void) | null = null

    const runHeartbeat = async () => {
      if (
        !AuthService.isAuth() ||
        document.visibilityState === 'hidden' ||
        AuthService.hasHeartbeatInFlight()
      ) {
        return
      }

      try {
        await AuthService.heartbeatSession(currentPlatform)
      } catch {
        // auth interceptor handles refresh and forced logout paths
      }
    }

    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        runHeartbeat()
      }
    }

    const handleFocus = () => {
      runHeartbeat()
    }

    runHeartbeat()
    heartbeatTimer = setInterval(runHeartbeat, HEARTBEAT_INTERVAL_MS)
    document.addEventListener('visibilitychange', handleVisible)
    window.addEventListener('focus', handleFocus)

    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          runHeartbeat()
        }
      }).then((listener) => {
        removeAppStateListener = () => {
          listener.remove()
        }
      })
    }

    return () => {
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer)
      }
      document.removeEventListener('visibilitychange', handleVisible)
      window.removeEventListener('focus', handleFocus)
      removeAppStateListener?.()
    }
  }, [isSessionRestored])

  return <>{!isLoading && children}</>
}
